import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import api from "../../api";

function names(list) {
  if (!list?.length) return "�";
  return list.join(", ");
}

export default function ConsortiumCoverage() {
  const [tab, setTab] = useState("coverage");
  const [coverage, setCoverage] = useState(null);
  const [issues, setIssues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [coverageRes, validationRes] = await Promise.all([
        api.get("admin/school-data/coverage"),
        api.get("admin/school-data/validation"),
      ]);
      setCoverage(coverageRes.data?.data ?? null);
      setIssues(validationRes.data?.data?.issues ?? []);
    } catch (e) {
      setError(e.response?.data?.message || "Could not load the data check.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = coverage?.rows ?? [];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Consortium coverage</Typography>
          <Typography sx={{ color: "#4b5563", fontSize: 14 }}>
            Each consortium, how many past papers, practice papers, and mock exams are linked, and which member schools have none.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={load} disabled={loading}>Refresh</Button>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {loading && <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>}

      {!loading && !error && (
        <>
          <Tabs value={tab} onChange={(_, value) => setTab(value)}>
            <Tab value="coverage" label="Coverage" />
            <Tab value="validation" label={`Validation (${issues?.length ?? 0})`} />
          </Tabs>

          {tab === "coverage" && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Consortium", "Type", "Schools", "Past papers", "Schools with no past papers", "Practice papers", "Schools with no practice papers", "Mock exams", "Schools with no mocks"].map((heading) => (
                      <TableCell key={heading} sx={{ fontWeight: 700 }}>{heading}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.name} hover>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.type || "�"}</TableCell>
                      <TableCell>{row.school_count}</TableCell>
                      <TableCell>{row.past_papers}</TableCell>
                      <TableCell>{names(row.schools_without_past_papers)}</TableCell>
                      <TableCell>{row.practice_papers}</TableCell>
                      <TableCell>{names(row.schools_without_practice_papers)}</TableCell>
                      <TableCell>{row.mock_exams}</TableCell>
                      <TableCell>{names(row.schools_without_mock_exams)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === "validation" && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Record</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Problem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(issues ?? []).map((issue, index) => (
                    <TableRow key={`${issue.entity}-${issue.name}-${index}`}>
                      <TableCell><Chip size="small" label={issue.entity} /></TableCell>
                      <TableCell>{issue.name}</TableCell>
                      <TableCell>{issue.problem}</TableCell>
                    </TableRow>
                  ))}
                  {!issues?.length && (
                    <TableRow>
                      <TableCell colSpan={3}>Every checked record passed.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}
